import { useEffect, useState, useCallback, useRef } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import './CheckinPage.css';
import OtaReservationForm from '../../components/admin/OtaReservationForm';
import IssuedUrlPanel from '../../components/admin/IssuedUrlPanel';
import type { OtaFormState, IssuedInfo } from '../../components/admin/otaTypes';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// 管理画面: 非対面チェックイン 本人確認（法令遵守：顔写真と旅券の確認）
//   設計: .plan/checkin_identity_verification_design.md
//   一覧 → 行の「確認」で詳細モーダル。宿泊者ごとに顔・旅券を並べて照合し、
//   承認(verified) / 再提出(resubmission_required) / 不一致(mismatch) / 要対応(needs_attention) を記録。
//   見た目は PassportPage（/admin/passport）に合わせる（admin-page / admin-table / モーダル / ライトボックス）。
// ------------------------------------------------------------

type VStatus = 'pending' | 'submitted' | 'verifying' | 'resubmission_required' | 'mismatch' | 'verified' | 'needs_attention';

interface FileRow { id: string; kind: 'face' | 'passport'; content_type: string; created_at: string; }
interface GuestRow {
  id: string; seq_no: number; name: string | null; birthdate: string | null; gender: string | null;
  nationality: string | null; passport_number: string | null; verification_status: VStatus;
  submit_source: string | null; verification_note: string | null; files: FileRow[];
}
interface Detail {
  reservation_id: string; property_name: string; checkin_date: string; checkout_date: string;
  guest_count: number; identity_verification_status?: string; checkin_status?: string; guests: GuestRow[];
}
interface ListRow {
  id: string; property_name: string; checkin_date: string; guest_count: number;
  verified_count: number; registered_count: number; identity_verification_status: string;
  checkin_status: string; status: string; cancelled?: boolean; booking_prefix?: string | null;
}

const STATUS_LABEL: Record<VStatus, string> = {
  get pending() { return tx('ci.0'); },
  get submitted() { return tx('ci.1'); },
  get verifying() { return tx('ci.2'); },
  get resubmission_required() { return tx('ci.3'); },
  get mismatch() { return tx('ci.4'); },
  get verified() { return tx('ci.5'); },
  get needs_attention() { return tx('ci.6'); },
};

const RSV_STATUS_LABEL: Record<string, string> = {
  get not_started() { return tx('ci.7'); },
  get in_progress() { return tx('ci.2'); },
  get completed() { return tx('ci.8'); },
  get needs_attention() { return tx('ci.6'); },
};

const GENDER_LABEL: Record<string, string> = { get male() { return tx('ci.9'); }, get female() { return tx('ci.10'); }, get other() { return tx('ci.11'); } };

function CheckinPage() {
  const [rows, setRows] = useState<ListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [showPast, setShowPast] = useState(false); // 既定は過去のチェックイン日を非表示
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [detail, setDetail] = useState<Detail | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejectModal, setRejectModal] = useState<{ guest: GuestRow; outcome: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const objectUrls = useRef<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch('/api/admin/passport/reservations', { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    setRows(d.reservations || []);
    setLoading(false);
  }, []);

interface PropertyOpt { id: string; name: string; }


  // URL発行 / OTA予約登録（PassportPage と同じバックエンド /api/admin/passport/* を再利用）
  const [copiedUrl, setCopiedUrl] = useState('');
  // チェックインURL発行結果。passport の issued とは別管理。
  //   url      : 施設コード+日付入り（/checkin?facility=XX&date=YYYY-MM-DD）。ゲストは入力不要。
  //   fixedUrl : 施設固定URL（/checkin?facility=XX）。日付なし。OTAの固定URL欄に貼れる。
  const [checkinUrl, setCheckinUrl] = useState<{ url: string; fixedUrl: string; facility: string; checkin: string } | null>(null);
  const [properties, setProperties] = useState<PropertyOpt[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [otaForm, setOtaForm] = useState<OtaFormState>({
    property_id: '', checkin_date: '', checkout_date: '', guest_count: 1,
    guest_name: '', source_channel: 'airbnb', ota_reservation_code: '',
  });
  const [issued, setIssued] = useState<IssuedInfo | null>(null);
  const [sending, setSending] = useState(false);
  const loadProps = useCallback(async () => {
    const res = await fetch('/api/admin/properties', { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    const list = (d.properties || d || []) as any[];
    setProperties(list.map((p) => ({ id: p.id, name: p.name })));
  }, []);

  useEffect(() => { load(); loadProps(); }, [load, loadProps]);
  // ===== URL発行関連（PassportPage と同一ロジック） =====
  async function copyUrl(text: string) {
    try { await navigator.clipboard.writeText(text); setCopiedUrl(text); setTimeout(() => setCopiedUrl(''), 2000); }
    catch { /* クリップボード不可環境では手動選択 */ }
  }

  // チェックインURL発行: ゲストに渡すチェックイン用URLを組み立てて表示する（コピー用）。
  //   形式: {origin}/checkin?facility={施設コード}&date={チェックイン日}
  //   施設コード(booking_prefix)＋チェックイン日をクエリに含めるため、ゲストは
  //   施設コードも日付も入力・選択せずにチェックイン本人確認へ進める。
  //   ※ passport の /issue・/rescue とは別物。メール送信や予約番号は使わない。
  function issueCheckinUrl(r: ListRow) {
    setMsg(''); setIssued(null); setCheckinUrl(null);
    const prefix = (r.booking_prefix || '').trim().toUpperCase();
    if (!prefix) {
      setMsg(tx('ci.12'));
      return;
    }
    const origin = window.location.origin.replace(/\/+$/, '');
    const fixedUrl = `${origin}/checkin?facility=${encodeURIComponent(prefix)}`;
    const url = `${fixedUrl}&date=${encodeURIComponent(r.checkin_date)}`;
    setCheckinUrl({ url, fixedUrl, facility: prefix, checkin: r.checkin_date });
    setMsg(tx('ci.13'));
  }

  // 発行済みの内容でメールを送信（自社予約のみ）
  async function sendMail(id: string) {
    setSending(true);
    try {
      const res = await fetch('/api/admin/passport/send-mail', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reservation_id: id, subject: issued?.mailSubject, text: issued?.mailText }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(tx('ci.14')); return; }
      setMsg(d.skipped ? tx('ci.15') : tx('ci.16'));
      setIssued(null);
    } finally { setSending(false); }
  }

  async function submitOta(e: React.FormEvent) {
    e.preventDefault();
    setMsg(''); setIssued(null);
    const res = await fetch('/api/admin/passport/ota-reservations', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(otaForm),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || tx('ci.17')); return; }
    setIssued({ reservationId: d.reservation_id, isOta: true, url: d.fixed_url, code: d.access_code, checkin: d.checkin_date });
    setMsg(tx('ci.18'));
    setShowForm(false);
    setOtaForm({ property_id: '', checkin_date: '', checkout_date: '', guest_count: 1, guest_name: '', source_channel: 'airbnb', ota_reservation_code: '' });
    await load();
  }

  useEffect(() => () => { objectUrls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const openDetail = async (id: string) => {
    setMsg('');
    const res = await fetch(`/api/admin/passport/reservations/${id}`, { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(tx('ci.19')); return; }
    setDetail(d);
    for (const g of d.guests || []) {
      for (const f of g.files || []) {
        if (!f.content_type?.startsWith('image/')) continue;
        try {
          const r = await fetch(`/api/admin/passport/files/${f.id}`, { headers: adminHeaders() });
          if (!r.ok) continue;
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          objectUrls.current.push(url);
          setThumbs((prev) => ({ ...prev, [f.id]: url }));
        } catch { /* ignore */ }
      }
    }
  };

  const act = async (guestId: string, path: string, body?: any) => {
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`/api/admin/passport/guests/${guestId}/${path}`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(tx('ci.20', { p0: d.error || res.status })); return; }
      if (detail) await openDetail(detail.reservation_id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const verify = (g: GuestRow) => act(g.id, 'verify');
  const openReject = (g: GuestRow, outcome: string) => { setRejectReason(''); setRejectModal({ guest: g, outcome }); };
  const confirmReject = async () => {
    if (!rejectModal) return;
    const { guest, outcome } = rejectModal;
    await act(guest.id, 'reject', { outcome, reason: rejectReason.trim() });
    setRejectModal(null);
    setRejectReason('');
  };

  const OUTCOME_LABEL: Record<string, string> = {
    resubmission_required: tx('ci.21'),
    mismatch: tx('ci.22'),
    needs_attention: tx('ci.23'),
  };

  const faceFile = (g: GuestRow) => g.files.find((f) => f.kind === 'face');
  const passportFile = (g: GuestRow) => g.files.find((f) => f.kind === 'passport');

  const renderThumb = (g: GuestRow, kind: 'face' | 'passport', caption: string) => {
    const f = kind === 'face' ? faceFile(g) : passportFile(g);
    return (
      <div className="ci-img-card">
        <span className="ci-img-cap">{caption}</span>
        {f && thumbs[f.id] ? (
          <img src={thumbs[f.id]} alt={caption} className="pp-thumb" onClick={() => setLightbox(thumbs[f.id])} title={tx('ci.24')} />
        ) : f ? (
          <div className="pp-thumb pp-thumb-loading">{tx('ci.25')}</div>
        ) : (
          <div className="pp-thumb ci-thumb-empty">{tx('ci.0')}</div>
        )}
      </div>
    );
  };

  // 物件名の選択肢（重複除去）
  const propertyNames = Array.from(new Set(rows.map((r) => r.property_name))).sort((a, b) => a.localeCompare(b, 'ja'));
  // 今日（JST）の YYYY-MM-DD
  const todayJst = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  // フィルター（物件・過去）→ ソート（チェックイン日）
  const visibleRows = rows
    .filter((r) => !propertyFilter || r.property_name === propertyFilter)
    .filter((r) => showPast || r.checkin_date >= todayJst)
    .slice()
    .sort((a, b) => {
      const cmp = a.checkin_date.localeCompare(b.checkin_date);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  function toggleSort() { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="admin-page-title">{tx('ci.26')}</h1>
        <button className="pp-btn" onClick={() => { setShowForm((v) => !v); setIssued(null); }}>
          {showForm ? tx('ci.27') : tx('ci.28')}
        </button>
      </div>
      <p className="ci-lead">{tx('ci.29')}<strong>{tx('ci.30')}</strong>{tx('ci.31')}<strong>{tx('ci.32')}</strong>{tx('ci.33')}</p>
      {msg && <p className="ci-msg">{msg}</p>}

      {/* チェックインURL発行結果（/checkin?facility=XX&date=YYYY-MM-DD）。ゲストに渡す用。 */}
      {checkinUrl && (
        <div className="pp-detail-overlay" onClick={() => setCheckinUrl(null)}>
        <div className="pp-detail pp-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
            <strong>{tx('ci.34')}</strong>
            <button type="button" className="pp-btn-ghost" onClick={() => setCheckinUrl(null)} aria-label={tx('ci.27')}>×</button>
          </div>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '1rem', display: 'grid', gap: '.6rem' }}>
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <span style={{ minWidth: 150, fontSize: '.85rem', color: '#374151' }}>{tx('ci.35')}</span>
            <input readOnly value={checkinUrl.url} className="pp-input" style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="pp-btn-sm" onClick={() => copyUrl(checkinUrl.url)}>{copiedUrl === checkinUrl.url ? tx('ci.36') : tx('ci.37')}</button>
          </div>
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <span style={{ minWidth: 150, fontSize: '.85rem', color: '#374151' }}>{tx('ci.38')}</span>
            <input readOnly value={checkinUrl.fixedUrl} className="pp-input" style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="pp-btn-sm" onClick={() => copyUrl(checkinUrl.fixedUrl)}>{copiedUrl === checkinUrl.fixedUrl ? tx('ci.36') : tx('ci.37')}</button>
          </div>
          <div style={{ fontSize: '.85rem', color: '#374151' }}>{tx('ci.39')} <strong>{checkinUrl.facility}</strong> {tx('ci.40')} <strong>{checkinUrl.checkin}</strong></div>
          <div style={{ fontSize: '.8rem', color: '#6b7280' }}>
            {tx('ci.41')}<br />
            {tx('ci.42')}
            <div style={{ marginTop: '.5rem', textAlign: 'right' }}>
              <button type="button" className="pp-btn-ghost" onClick={() => setCheckinUrl(null)}>{tx('ci.27')}</button>
            </div>
          </div>
        </div>
        </div>
        </div>
      )}

      {/* URL発行結果パネル / OTA予約登録フォーム（3画面共通コンポーネント） */}
      {issued && (
        <div className="pp-detail-overlay" onClick={() => { if (!sending) setIssued(null); }}>
        <div className="pp-detail pp-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
            <strong>{tx('ci.43')}</strong>
            <button type="button" className="pp-btn-ghost" onClick={() => setIssued(null)} disabled={sending} aria-label={tx('ci.27')}>×</button>
          </div>
        <IssuedUrlPanel
          issued={issued}
          copiedUrl={copiedUrl}
          onCopy={copyUrl}
          onClose={() => setIssued(null)}
          onEditSubject={(v) => setIssued((prev) => prev ? { ...prev, mailSubject: v } : prev)}
          onEditText={(v) => setIssued((prev) => prev ? { ...prev, mailText: v } : prev)}
          onSendMail={sendMail}
          sending={sending}
        />
        </div>
        </div>
      )}
      {showForm && (
        <OtaReservationForm form={otaForm} properties={properties} onChange={setOtaForm} onSubmit={submitOta} />
      )}

      {/* フィルターバー: 物件名 / 過去日程の表示切替（PassportPage と同じ） */}
      {!loading && (
        <div className="pp-filter-bar">
          <label className="pp-filter-item">
            <span className="pp-filter-label">{tx('ci.44')}</span>
            <select className="pp-input" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
              <option value="">{tx('ci.45')}</option>
              {propertyNames.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="pp-filter-check">
            <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
            <span>{tx('ci.46')}</span>
          </label>
          <span className="pp-filter-count">{visibleRows.length}{tx('ci.47')}</span>
        </div>
      )}

      {loading ? (
        <p>{tx('ci.25')}</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th className="pp-sortable" onClick={toggleSort} title={tx('ci.48')}>{tx('ci.49')} {sortDir === 'asc' ? '▲' : '▼'}</th>
              <th>{tx('ci.50')}</th>
              <th>{tx('ci.51')}</th>
              <th>{tx('ci.52')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>{tx('ci.53')}</td></tr>
            )}
            {visibleRows.map((r) => (
              <tr key={r.id}>
                <td data-label={tx('ci.49')}>{r.checkin_date}</td>
                <td data-label={tx('ci.50')}>{r.property_name}</td>
                <td data-label={tx('ci.51')}><span className={`ci-badge st-${r.identity_verification_status || 'not_started'}`}>{RSV_STATUS_LABEL[r.identity_verification_status] || tx('ci.7')}</span></td>
                <td data-label={tx('ci.52')}>
                  {(r.verified_count ?? 0) >= r.guest_count
                    ? <span className="pp-done">{tx('ci.8')} {r.verified_count ?? 0}/{r.guest_count}</span>
                    : <span className="pp-pending">{r.verified_count ?? 0}/{r.guest_count}</span>}
                </td>
                <td data-label={tx('ci.54')} className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                  <button className="pp-btn-sm" onClick={() => openDetail(r.id)}>{tx('ci.55')}</button>
                  {' '}
                  {!r.cancelled && <button className="pp-btn-sm" onClick={() => issueCheckinUrl(r)} title={tx('ci.56')}>{tx('ci.57')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 詳細モーダル（PassportPage と同じ見た目） */}
      {detail && (
        <div className="pp-detail-overlay" onClick={() => setDetail(null)}>
          <div className="pp-detail pp-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{detail.property_name}{tx('ci.58')}{detail.reservation_id}{tx('ci.59')}</h2>
              <button className="pp-btn-ghost" onClick={() => setDetail(null)}>{tx('ci.27')}</button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '.9rem' }}>
              {detail.checkin_date} 〜 {detail.checkout_date} / {detail.guest_count}名
              {' / '}{tx('ci.61')} <strong>{RSV_STATUS_LABEL[detail.identity_verification_status || 'not_started']}</strong>
            </p>

            {detail.guests.map((g) => (
              <div key={g.id} className={`ci-guest st-${g.verification_status}`}>
                <div className="ci-guest-head">
                  <strong>{tx('ci.62')}{g.seq_no}{g.name ? tx('ci.63', { p0: g.name }) : tx('ci.64')}</strong>
                  <span className={`ci-badge st-${g.verification_status}`}>{STATUS_LABEL[g.verification_status]}</span>
                  {g.submit_source && <span className="ci-src">{tx('ci.65')} {g.submit_source === 'tablet' ? tx('ci.66') : tx('ci.67')}</span>}
                </div>

                <div className="ci-guest-body">
                  <div className="ci-img-pair">
                    {renderThumb(g, 'face', tx('ci.30'))}
                    {renderThumb(g, 'passport', tx('ci.32'))}
                  </div>
                  <div className="ci-guest-info">
                    <div>{tx('ci.68')} {g.birthdate || '—'} {tx('adm.gender')} {GENDER_LABEL[g.gender || ''] || '—'}</div>
                    <div>{tx('ci.69')} {g.nationality || '—'} {tx('adm.passportNo')} {g.passport_number || '—'}</div>
                    {g.verification_note && <div className="ci-note">{tx('ci.70')} {g.verification_note}</div>}
                  </div>
                </div>

                <div className="ci-actions">
                  <button className="pp-btn" disabled={busy || !['submitted', 'verifying'].includes(g.verification_status)} onClick={() => verify(g)}>
                    {tx('ci.71')}
                  </button>
                  <button className="pp-btn-ghost" disabled={busy} onClick={() => openReject(g, 'resubmission_required')}>{tx('ci.21')}</button>
                  <button className="pp-btn-ghost ci-danger" disabled={busy} onClick={() => openReject(g, 'mismatch')}>{tx('ci.4')}</button>
                  <button className="pp-btn-ghost ci-danger" disabled={busy} onClick={() => openReject(g, 'needs_attention')}>{tx('ci.6')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 画像拡大（ライトボックス。PassportPage と同じ） */}
      {lightbox && (
        <div className="pp-lightbox" onClick={() => setLightbox(null)}>
          <button className="pp-lightbox-close" onClick={() => setLightbox(null)} aria-label={tx('ci.27')}>×</button>
          <img src={lightbox} alt={tx('ci.72')} className="pp-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* 差し戻し理由モーダル */}
      {rejectModal && (
        <div className="checkin-modal-overlay" onClick={() => !busy && setRejectModal(null)}>
          <div className="checkin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{OUTCOME_LABEL[rejectModal.outcome] || tx('ci.73')}</h3>
            <p className="modal-target">
              {tx('ci.62')} {rejectModal.guest.seq_no}{rejectModal.guest.name ? tx('ci.63', { p0: rejectModal.guest.name }) : ''}
            </p>
            <label className="modal-label">
              {tx('ci.74')}{rejectModal.outcome === 'resubmission_required' ? tx('ci.75') : ''}
              <textarea rows={3} value={rejectReason} maxLength={1000}
                placeholder={rejectModal.outcome === 'mismatch' ? tx('ci.76') : rejectModal.outcome === 'needs_attention' ? tx('ci.77') : tx('ci.78')}
                onChange={(e) => setRejectReason(e.target.value)} />
            </label>
            <div className="checkin-modal-actions">
              <button type="button" className="pp-btn-ghost" disabled={busy} onClick={() => setRejectModal(null)}>{tx('ci.79')}</button>
              <button type="button" className="pp-btn"
                disabled={busy || (rejectModal.outcome !== 'resubmission_required' && !rejectReason.trim())}
                onClick={confirmReject}>{tx('ci.80')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckinPage;
